// zod copies the ZodType prototype methods onto every schema instance when it is constructed, so the
// extensions must be registered before the shared schema constants below are built.
import './zod-extensions';
import {z, ZodType} from 'zod';
import {DateTime} from 'luxon';
import {Observable, pipe, UnaryFunction} from 'rxjs';
import {map} from 'rxjs/operators';

export const zDateTime = z.string().transform((val, ctx) => {
    const date = DateTime.fromISO(val);
    if (!date.isValid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid DateTime: ${val}`,
        });
        return z.NEVER;
    }
    return date;
});

/**
 * A calendar date, openapi `format: date` (`yyyy-MM-dd`). A date sent as a timestamp shows the
 * previous day in zones west of the sender, so the backend owns turning its instant into a date
 * (requested on AGI-19637). Until every producer sends plain dates, an instant is tolerated and
 * read in Europe/Copenhagen - the zone Dynamics 365 stores Danish calendar dates in - which
 * yields the same day the plain date will. Use {@link zDateTime} for a genuine point in time.
 */
export const zDateOnly = z.string().transform((val, ctx) => {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(val)
        ? DateTime.fromISO(val)
        : DateTime.fromISO(val, {zone: 'Europe/Copenhagen'});
    if (!date.isValid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid date: ${val}`,
        });
        return z.NEVER;
    }
    return date;
});

/**
 * A date or timestamp kept in the offset the backend sent it in, so every browser shows the
 * building's day and time instead of converting it to its own zone. A plain `yyyy-MM-dd` date
 * has no offset and reads the same everywhere.
 */
export const zDateTimeAsSent = z.string().transform((val, ctx) => {
    const date = DateTime.fromISO(val, {setZone: true});
    if (!date.isValid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid DateTime: ${val}`,
        });
        return z.NEVER;
    }
    return date;
});

export const zWbBoolean = z
    .enum(['N', 'J', 'Y'] as const)
    .transform(val => val === 'J' || val === 'Y');

export const zStringToNumber = z.string().transform(val => Number(val));

export const zNumberToString = z.number().transform(val => val.toString(10));

// Some endpoints send an id as a JSON number where their siblings send a string, so neither
// `zNumberToString` nor a bare `z.string()` accepts every response.
export const zIdToString = z.union([z.string(), z.number()]).transform(value => String(value));

export const parseList =
    <Output>(schema: ZodType<Output, object>) =>
    (items: object): Output[] =>
        z.array(schema).parse(items);

export const mapParseList = <Output>(
    schema: ZodType<Output, object>,
): UnaryFunction<Observable<object>, Observable<Output[]>> => pipe(map(parseList(schema)));

export const mapParseItem = <Output>(
    schema: ZodType<Output, object>,
): UnaryFunction<Observable<object>, Observable<Output>> => pipe(map(item => schema.parse(item)));

export const readStoredJson = <Output>(
    key: string,
    schema: ZodType<Output, object>,
    fallback: Output,
): Output => {
    const raw = localStorage.getItem(key);
    if (raw === null) {
        return fallback;
    }
    try {
        const result = schema.safeParse(JSON.parse(raw));
        return result.success ? result.data : fallback;
    } catch {
        return fallback;
    }
};
